import React from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from "recharts";

const DATA = [
  { month: "Mar \'24", rate: 3.6, forecast: null },
  { month: "Apr \'24", rate: 3.0, forecast: null },
  { month: "May \'24", rate: 2.4, forecast: null },
  { month: "Jun \'24", rate: 2.0, forecast: null },
  { month: "Jul \'24", rate: 2.2, forecast: null },
  { month: "Aug \'24", rate: 2.5, forecast: null },
  { month: "Sep \'24", rate: 2.9, forecast: null },
  { month: "Oct \'24", rate: 3.4, forecast: null },
  { month: "Nov \'24", rate: 3.9, forecast: null },
  { month: "Dec \'24", rate: 4.5, forecast: null },
  { month: "Jan \'25", rate: 4.1, forecast: null },
  { month: "Feb \'25", rate: 3.6, forecast: null },
  { month: "Mar \'25", rate: 2.8, forecast: 2.8 },
  { month: "Apr \'25", rate: null, forecast: 2.4 },
  { month: "May \'25", rate: null, forecast: 2.0 },
  { month: "Jun \'25", rate: null, forecast: 1.8 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const actual = payload?.find(p => p?.dataKey === "rate");
  const forecast = payload?.find(p => p?.dataKey === "forecast");
  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-lg">
      <p className="font-heading font-semibold text-sm text-foreground mb-1">{label}</p>
      {actual?.value != null && (
        <p className="text-xs font-caption text-foreground">
          Actual: <span className="font-semibold">{actual?.value} mo</span>
        </p>
      )}
      {forecast?.value != null && (
        <p className="text-xs font-caption text-blue-600">
          Forecast: <span className="font-semibold">{forecast?.value} mo</span>
        </p>
      )}
      <p className="text-xs font-caption text-muted-foreground mt-1">
        {(actual?.value || forecast?.value) <= 3 ? "🔥 Seller's Market" :
         (actual?.value || forecast?.value) <= 6 ? "⚖️ Balanced Market" : "🏠 Buyer's Market"}
      </p>
    </div>
  );
};

export default function AbsorptionRateChart() {
  return (
    <div className="card-base flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
        <div>
          <h3 className="font-heading font-semibold text-base text-foreground">Market Absorption Rate</h3>
          <p className="text-xs font-caption text-muted-foreground mt-0.5">Months of inventory with 4-month forecast</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-primary rounded" />
            <span className="text-xs font-caption text-muted-foreground">Actual</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-blue-400 rounded border-dashed" style={{ borderTop: "2px dashed #60A5FA", background: "none" }} />
            <span className="text-xs font-caption text-muted-foreground">Forecast</span>
          </div>
        </div>
      </div>
      <div className="w-full h-48" aria-label="Market absorption rate area chart with forecast">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={DATA} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="rateGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.12} />
                <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fontFamily: "Inter", fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fontFamily: "Inter", fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={28}
              tickFormatter={v => `${v}mo`}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={3} stroke="var(--color-success)" strokeDasharray="4 2" strokeWidth={1.5}
              label={{ value: "Seller's", position: "right", fontSize: 9, fill: "var(--color-success)" }} />
            <ReferenceLine y={6} stroke="var(--color-warning)" strokeDasharray="4 2" strokeWidth={1.5}
              label={{ value: "Buyer's", position: "right", fontSize: 9, fill: "var(--color-warning)" }} />
            <Area
              type="monotone"
              dataKey="rate"
              stroke="var(--color-primary)"
              strokeWidth={2.5}
              fill="url(#rateGrad)"
              connectNulls={false}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Area
              type="monotone"
              dataKey="forecast"
              stroke="#60A5FA"
              strokeWidth={2}
              strokeDasharray="5 3"
              fill="url(#forecastGrad)"
              connectNulls={false}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
        {[
          { label: "Current Rate", value: "2.8 mo", status: "Seller's Market", color: "text-emerald-600" },
          { label: "6mo Forecast", value: "1.8 mo", status: "Hot Market", color: "text-emerald-700" },
          { label: "YoY Change", value: "-0.8 mo", status: "Tightening", color: "text-blue-600" },
        ]?.map(item => (
          <div key={item?.label} className="text-center">
            <p className="text-xs font-caption text-muted-foreground">{item?.label}</p>
            <p className={`font-mono-data font-bold text-sm ${item?.color}`}>{item?.value}</p>
            <p className="text-xs font-caption text-muted-foreground">{item?.status}</p>
          </div>
        ))}
      </div>
    </div>
  );
}